import type { FollowupFieldViewDecisionEnum } from "@/lib/api/generated/casedb";
import type { FollowupRecordState } from "../types/followupReviewView";

export const FOLLOWUP_PANEL_ID = "panel.followup_changes" as const;
export const FOLLOWUP_PANEL_TITLE = "Follow-up changes";
export const FOLLOWUP_EMPTY_MESSAGE = "No follow-up reports are attached to this case.";
export const FOLLOWUP_PENDING_LABEL = "Pending";
export const FOLLOWUP_ALL_DECISIONS_LABEL = "All decisions";
// "Parent" and "Follow-up", not "Current" and "Incoming" (issue 298, Soumya via Harshit; Raja, 2026-10-11).
export const FOLLOWUP_ACCEPT_LABEL = "Use follow-up";
export const FOLLOWUP_KEEP_LABEL = "Keep parent";
export const FOLLOWUP_EDIT_LABEL = "Use edited value";
/** An incoming record's answers, in its record type's words (Raja, 2026-10-11: business wording, not "record"). */
export const recordNoun = (item: string): string => item.charAt(0).toLowerCase() + item.slice(1);
export const followupMatchLabel = (noun: string): string => `Same as existing ${noun}`;
export const followupCreateLabel = (noun: string): string => `Add as new ${noun}`;
export const followupCreatedLabel = (noun: string): string => `New ${noun}`;
/** "Same as Rhabdomyolysis" once the reviewer picked the case's record. */
export const followupMatchedToLabel = (title: string): string => `Same as ${title}`;
export const followupParentFirstLabel = (noun: string): string => `Answer the ${noun} first`;
export const FOLLOWUP_MATCHED_ON_SAVE_LABEL = "Matched when you save";
export const FOLLOWUP_EXCLUDE_LABEL = "Don't add";
export const FOLLOWUP_EXCLUDED_LABEL = "Not added";
export const FOLLOWUP_UNDO_LABEL = "Undo";
export const FOLLOWUP_FIELD_LOCKED_BY_PROPOSAL = "Decide the follow-up proposal to change this value.";
export const FOLLOWUP_OPEN_IN_CASE_LABEL = "Open in case";
export const FOLLOWUP_INCOMING_RECORD_LABEL = "Follow-up record";
export const FOLLOWUP_SOURCE_LABEL = "Show in source";
export const FOLLOWUP_CURRENT_SOURCE_LABEL = "Show parent in source";
export const FOLLOWUP_CURRENT_VALUE_LABEL = "Parent";
export const FOLLOWUP_INCOMING_VALUE_LABEL = "Follow-up";
export const FOLLOWUP_RECEIVED_PREFIX = "received";
export const FOLLOWUP_ORIGINAL_DOCUMENT_LABEL = "Original";
export const FOLLOWUP_BASE_VERSION_DOCUMENT_LABEL = "Previous version";
export const FOLLOWUP_SWITCHER_LABEL = "Source document";
export const FOLLOWUP_BADGE_PREFIX = "F/U";
export const FOLLOWUP_ARRIVALS_LABEL = "Follow-up arrivals";
/** The accessible name of an arrival's recorded details: "Follow-up 2 details". */
export const followupArrivalDetailsLabel = (arrivalLabel: string): string => `${arrivalLabel} details`;
export const FOLLOWUP_FILTER_LABEL = "Follow-up filter";
/** The incoming value exists on the follow-up case but no AI review row stands behind it (coded, derived, defaulted or looked up). */
export const FOLLOWUP_NOT_EXTRACTED_LABEL = "Not extracted";
/** Column heads of the comparison table a follow-up proposal is decided in. */
export const FOLLOWUP_TABLE_HEAD = ["Field", FOLLOWUP_CURRENT_VALUE_LABEL, FOLLOWUP_INCOMING_VALUE_LABEL, "AI confidence", "Action"] as const;
/** Column heads of an unresolved incoming record: nothing to compare with yet. */
export const FOLLOWUP_INCOMING_TABLE_HEAD = ["Field", FOLLOWUP_INCOMING_VALUE_LABEL] as const;
/** Required fields a record being created needs that the follow-up did not include: the reviewer types them. */
export const FOLLOWUP_ADDED_FIELDS_LABEL = "Required, not in the follow-up";
export const FOLLOWUP_ADDED_FIELDS_TABLE_HEAD = ["Field", "Your value"] as const;
/** `FollowupRecordState` values — matched by `caseReview.css` on the record chip, so a contract. */
export const FOLLOWUP_RECORD_STATES = { incoming: "incoming", matched: "matched", created: "created", excluded: "excluded" } as const;
/** The chip subtitle for each record state. */
export const FOLLOWUP_RECORD_STATE_LABELS: Record<FollowupRecordState, string> = {
  incoming: FOLLOWUP_INCOMING_VALUE_LABEL, matched: "Matched", created: "Created", excluded: "Excluded",
};
export const FOLLOWUP_UNSAVED_TAG = "unsaved";
export const FOLLOWUP_UNRESOLVED_LABEL = "Unresolved";
export const followupPendingCount = (pending: number): string => `${pending} pending`;
export const FOLLOWUP_REVIEWED_LABEL = "reviewed";

/** `FollowupBadge`'s `data-state` values — matched by `caseReview.css`, so they are a contract, not free text. */
export const FOLLOWUP_BADGE_STATES = { pending: "pending", decided: "decided" } as const;

/** `allDirty` key prefixes, so a follow-up dirty key can be told apart from a section one. */
export const FOLLOWUP_FIELD_DIRTY_PREFIX = "followup:";
export const FOLLOWUP_RECORD_DIRTY_PREFIX = "followup-record:";

/** "Follow-up 1" — the arrival's 1-based ordinal in id order. `arrival.version` is the target case's business version, not this number. */
export const followupArrivalLabel = (ordinal: number): string => `Follow-up ${ordinal}`;

/** "F/U 1" or "F/U 1, 2" on a field several arrivals touched. */
export const followupBadgeLabel = (ordinals: number[]): string => `${FOLLOWUP_BADGE_PREFIX} ${ordinals.join(", ")}`;

/** Header chip: "Follow-up 1 · 12 pending", "2 follow-ups · 3 pending", "Follow-up 1 · reviewed". */
export const followupChipLabel = (arrivalCount: number, pending: number): string => {
  const head = arrivalCount === 1 ? followupArrivalLabel(1) : `${arrivalCount} follow-ups`;
  return pending > 0 ? `${head} · ${pending} pending` : `${head} · ${FOLLOWUP_REVIEWED_LABEL}`;
};

export const followupPendingOfTotal = (pending: number, total: number): string => `${pending} pending of ${total}`;

export const FOLLOWUP_DECISION_LABELS: Record<FollowupFieldViewDecisionEnum, string> = {
  PENDING: "Pending",
  ACCEPTED: "Accepted",
  EDITED: "Edited",
  KEPT: "Kept",
  UNCHANGED: "Unchanged",
  EXCLUDED: "Excluded",
  INHERITED: "Inherited",
};

/** The arrival receipt date correction (US-SLA-05): a control in the arrival details and its dialog. */
export const ARRIVAL_DUE_LABEL = "Due date";
export const ARRIVAL_CORRECT_RECEIPT_LABEL = "Correct receipt date";
export const ARRIVAL_CORRECT_RECEIPT_DISABLED_TITLE = "Start editing and save or discard your changes first.";
export const ARRIVAL_CORRECTION_TITLE = "Correct the follow-up receipt date";
export const ARRIVAL_CORRECTION_DESCRIPTION =
  "The due dates of this follow-up are calculated again from the corrected date. Other follow-ups keep their dates.";
export const ARRIVAL_CORRECTION_DATE_LABEL = "Received date";
export const ARRIVAL_CORRECTION_REASON_LABEL = "Reason for the correction";
export const ARRIVAL_CORRECTION_SUBMIT_LABEL = "Save correction";
export const ARRIVAL_CORRECTION_SAVING_LABEL = "Saving…";
export const ARRIVAL_CORRECTION_CANCEL_LABEL = "Cancel";
export const ARRIVAL_CORRECTION_DATE_REQUIRED = "Enter the received date.";
export const ARRIVAL_CORRECTION_FUTURE_DATE = "The received date cannot be in the future.";
export const ARRIVAL_CORRECTION_REASON_REQUIRED = "Enter a reason for the correction.";
/** The accessible name of an arrival's due date block: "Follow-up 2 due date". */
export const arrivalDueDateLabel = (arrivalLabel: string): string => `${arrivalLabel} due date`;
